import { useState } from 'react';
import { EventSections } from '../../lib/events/EventLogger';
import { IsDefaultAddress } from '../../lib/helpers/IsDefaultAddress';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { Customer } from '~/lib/api/smartsend/types';
import useGoto from '../../../../hooks/use-goto';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import { P2GImageMessage } from '~/components/imagemessage';
import { AddCollectionAddress } from '../onboarding/components/add-collection-address';
import { P2GContainer } from '@parcel2go/core-ui';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { useAchievements } from '~/hooks/use-achievements';
import useOnLoad from '~/routes/use-on-load';
import NewSmartSendUserPrompt from './NewSmartSendUserPrompt';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props {
  children?: React.ReactNode;
  setCustomer: (customer: Customer) => void;
  initialCustomer?: Customer;
  isXs: boolean;
  silent: boolean;
}

const CustomerWrapper: React.FC<props> = (props) => {
  const [loading, setLoading] = useState<'LOADING' | 'LOADED' | 'ERROR' | 'NOCUSTOMER'>('LOADING');
  const [creating, setCreating] = useState(false);
  const { getCustomers, createDefaultCustomer } = useSmartSendApi();
  const { addAchievements } = useAchievements();
  const { track } = useSmartSendAnalytics();
  const { goTo } = useGoto();

  useOnLoad(() => {
    const begin = async () => {
      setLoading('LOADING');
      const customers = await getCustomers();
      if (customers && customers.success) {
        if (customers.result && customers.result.Customers?.length > 0) {
          props.setCustomer(customers.result.Customers.find(IsDefaultAddress) || customers.result.Customers[0]);
          setLoading('LOADED');
        } else {
          track({
            type: 'onboarding_loaded',
            payload: {
              ...GenerateBaseAnalyticsPayload([EventSections.SmartSendSetup])
            }
          });
          setLoading('NOCUSTOMER');
        }
      } else {
        setLoading('ERROR');
        setTimeout(begin, 10000);
      }
    };
    begin();
  });

  const finish = async (customer: Customer, link: boolean) => {
    setCreating(true);
    const res = await createDefaultCustomer(customer);
    if (res.success && res.result?.Customer) {
      // Legacy Event
      if (!props.silent) {
        addAchievements([EventSections.Onboarding], ['SmartSendSignup']);
      }

      props.setCustomer(res.result.Customer);
      setLoading('LOADED');
      if (link) {
        goTo(RoutingConstants.AccountLinking);
      }
    } else {
      setLoading('ERROR');
    }
  };

  if (loading === 'LOADING') {
    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/loading-customer-details.png"
        message="Loading your details..."
        contentKey="image-smartsend-loading"
        showLoader
      />
    );
  }

  if (loading === 'ERROR') {
    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/no-customer-details.png"
        message="We're still trying to find your details..."
        contentKey="image-smartsend-loading"
        showLoader
      />
    );
  }

  if (loading === 'NOCUSTOMER') {
    return (
      <NewSmartSendUserPrompt>
        <P2GContainer maxWidth="xs">
          <AddCollectionAddress
            isXs={props.isXs}
            creating={creating}
            finish={finish}
            initialCustomer={props.initialCustomer}
          />
        </P2GContainer>
      </NewSmartSendUserPrompt>
    );
  }

  return <>{props.children}</>;
};

export default CustomerWrapper;
